import { useEffect, useState } from 'react';
import { useTournament } from '@shootout-comps/core';
import type { PublicTournamentSummary } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../components/SnookerBall';

const STATUS_COLOR: Record<string, string> = {
  registration: '#3498db', active: '#2ecc71', complete: 'var(--gold)',
};

const FORMAT_BADGE: Record<string, { label: string; icon: string; color: string }> = {
  snooker900: { label: 'Snooker 900', icon: '🕐', color: '#3498db' },
  standard:   { label: 'Standard', icon: '🔴', color: '#9b59b6' },
  shootout:   { label: 'Shootout', icon: '⚡', color: '#e67e22' },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface PublicTournamentsPageProps {
  onSelectTournament: (id: string) => void;
}

export default function PublicTournamentsPage({ onSelectTournament }: PublicTournamentsPageProps) {
  const { fetchPublicTournaments } = useTournament();
  const { t } = useLang();
  const [tournaments, setTournaments] = useState<PublicTournamentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPublicTournaments().then(setTournaments).catch((e: Error) => setError(e.message));
  }, [fetchPublicTournaments]);

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.5rem)', color: 'var(--gold)', marginBottom: '0.3rem' }}>
        🌐 {t.publicTournamentsHeading}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.75rem', lineHeight: 1.6 }}>
        {t.publicTournamentsSubtitle}
      </p>

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {tournaments === null && !error && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>
          <SnookerBallSpinner size={32} />
        </div>
      )}

      {tournaments && tournaments.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '2px dashed rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌐</div>
          <p style={{ fontSize: '0.88rem' }}>{t.noPublicTournaments}</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {(tournaments ?? []).map((tt, i) => {
          const realPlayers = tt.players.filter((p) => !p.isBye);
          const badge = FORMAT_BADGE[tt.format ?? 'shootout'];
          const statusColor = STATUS_COLOR[tt.status] ?? 'var(--chalk-dim)';
          return (
            <div key={tt._id} onClick={() => onSelectTournament(tt._id)}
              style={{ display: 'flex', alignItems: 'center', gap: '1.1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.15rem', cursor: 'pointer', transition: 'all 0.15s', animation: `fadeIn 0.3s ease ${i * 0.04}s both` }}
              className="public-tournament-row"
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tt.name}</span>
                  {badge && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, background: `${badge.color}22`, color: badge.color, border: `1px solid ${badge.color}55`, borderRadius: '4px', padding: '0.1rem 0.4rem' }}>
                      {badge.icon} {badge.label}
                    </span>
                  )}
                  {tt.isHandicap && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'rgba(46,204,113,0.1)', color: '#2ecc71', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '4px', padding: '0.1rem 0.4rem' }}>
                      🏅 {t.handicapTournament}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                  {tt.ownerUsername && <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>{t.hostedByLabel} {tt.ownerUsername}</span>}
                  <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>📅 {formatDate(tt.scheduledAt || tt.createdAt)}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>👥 {realPlayers.length}{tt.maxPlayers ? `/${tt.maxPlayers}` : ''}</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', flexShrink: 0 }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusColor }} />
                <span style={{ fontSize: '0.6rem', color: statusColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tt.status}</span>
              </div>
              <span style={{ color: 'var(--chalk-dim)', fontSize: '1rem' }}>›</span>
            </div>
          );
        })}
      </div>

      <style>{`.public-tournament-row:hover{transform:translateY(-1px);box-shadow:0 4px 16px rgba(0,0,0,0.3)} @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
